'use client';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from '@epihub/web/src/tailwind/fn/cn';
import type { PropsWithClassName } from '@epihub/web/src/react/types';

export type WordItem = {
  text?: string;
  Wrapper?: (props: { children: ReactNode }) => ReactNode;
  style?: CSSProperties;
  className?: string;
  ms?: number;
};

type TextGenerateEffectProps = {
  words: WordItem[];
  filter?: boolean;
  fadeDuration?: number;
  defaultMsPerWord?: number;
};

type ProcessedSegment = {
  segmentIndex: number;
  words: string[];
  style?: CSSProperties;
  className?: string;
  Wrapper?: (props: { children: ReactNode }) => ReactNode;
  msPerWord: number;
  globalStartIndex: number;
};

type ProcessedData = {
  segments: ProcessedSegment[];
  totalWords: number;
  delays: number[]; // delay for each global word index
};

const DEFAULT_MS_PER_WORD = 200;
const DEFAULT_FADE_DURATION = 0.3;

function processWords(items: WordItem[], defaultMsPerWord: number): ProcessedData {
  const segments: ProcessedSegment[] = [];
  const delays: number[] = [];
  let globalIndex = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (!item.text) {
      // Pause item - add a placeholder segment with delay
      const pauseMs = item.ms ?? defaultMsPerWord;
      delays.push(pauseMs);
      segments.push({
        segmentIndex: i,
        words: [],
        msPerWord: pauseMs,
        globalStartIndex: globalIndex,
      });
      globalIndex++;
    } else {
      const words = item.text.split(/\s+/).filter(Boolean);
      const msPerWord = item.ms != null ? item.ms / words.length : defaultMsPerWord;

      segments.push({
        segmentIndex: i,
        words,
        style: item.style,
        className: item.className,
        Wrapper: item.Wrapper,
        msPerWord,
        globalStartIndex: globalIndex,
      });

      for (const _ of words) {
        delays.push(msPerWord);
        globalIndex++;
      }
    }
  }

  return { segments, totalWords: globalIndex, delays };
}

const TextGenerateEffectInner = ({
  words,
  className,
  filter = true,
  fadeDuration = DEFAULT_FADE_DURATION,
  defaultMsPerWord = DEFAULT_MS_PER_WORD,
}: PropsWithClassName<TextGenerateEffectProps>) => {
  const processed = useMemo(() => processWords(words, defaultMsPerWord), [words, defaultMsPerWord]);

  const [visibleCount, setVisibleCount] = useState(1);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processedRef = useRef(processed);

  useEffect(() => {
    processedRef.current = processed;
    setVisibleCount(1);
  }, [processed]);

  useEffect(() => {
    if (visibleCount > processedRef.current.totalWords) {
      return;
    }

    const delay = processedRef.current.delays[visibleCount - 1] ?? defaultMsPerWord;

    timeoutRef.current = setTimeout(() => {
      setVisibleCount((c) => c + 1);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [visibleCount, defaultMsPerWord]);

  return (
    <div className={cn('font-bold', className)}>
      <div className='mt-4'>
        <div className='dark:text-white text-black text-2xl leading-snug tracking-wide'>
          <div>
            {processed.segments.map((segment) => {
              // Skip pause segments (no words)
              if (segment.words.length === 0) {
                return null;
              }

              const segmentContent = (
                <>
                  {segment.words.map((word, wordIdx) => {
                    const globalIdx = segment.globalStartIndex + wordIdx;
                    const isVisible = globalIdx < visibleCount;

                    return (
                      <span key={wordIdx}>
                        <motion.span
                          className={segment.className}
                          style={{ ...segment.style, display: 'inline-block' }}
                          initial={{ opacity: 0, filter: filter ? 'blur(10px)' : 'none' }}
                          animate={{
                            opacity: isVisible ? 1 : 0,
                            filter: isVisible ? 'blur(0px)' : (filter ? 'blur(10px)' : 'none'),
                          }}
                          transition={{ duration: fadeDuration }}
                        >
                          {word}
                        </motion.span>
                        {' '}
                      </span>
                    );
                  })}
                </>
              );

              return (
                <span key={segment.segmentIndex}>
                  {segment.Wrapper ? <segment.Wrapper>{segmentContent}</segment.Wrapper> : segmentContent}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TextGenerateEffect = memo(TextGenerateEffectInner);
